// Device stamp — what hardware + iOS version is the user on?
// Used to tag uploads so admin/debug views can see "iPhone 14, iOS 17.5".
//
// Cached after the first call (device info doesn't change at runtime).

import { Device } from "@capacitor/device";
import { isNative, platform } from "./platform";

export type DeviceStamp = {
  platform: "ios" | "android" | "web";
  /** e.g. "17.5.1" on iOS, "14.0" on Android, browser UA string fragment on web */
  osVersion?: string;
  /** e.g. "iPhone15,2" on iOS, browser name on web */
  model?: string;
  /** App bundle version when available */
  appVersion?: string;
};

let cached: DeviceStamp | null = null;

export async function getDeviceStamp(): Promise<DeviceStamp> {
  if (cached) return cached;
  const plat = platform();
  if (!isNative()) {
    cached = { platform: "web", model: navigator.userAgent.slice(0, 128) };
    return cached;
  }
  try {
    const info = await Device.getInfo();
    let appVersion: string | undefined;
    try {
      const { App } = await import("@capacitor/app");
      const i = await App.getInfo();
      appVersion = i.version;
    } catch {
      /* not in shipped binary — ignore */
    }
    cached = {
      platform: (plat === "ios" || plat === "android" ? plat : "web") as DeviceStamp["platform"],
      osVersion: info.osVersion,
      model: info.model,
      appVersion,
    };
    return cached;
  } catch {
    cached = { platform: (plat === "ios" || plat === "android" ? plat : "web") as DeviceStamp["platform"] };
    return cached;
  }
}
